'use client';

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Stack,
  Divider,
  Chip,
  CircularProgress,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import { Iconify } from 'src/components/iconify';
import { useSnackbar } from 'src/components/snackbar';
import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';
import axios, { endpoints } from 'src/lib/axios';
import { fToNow } from 'src/utils/format-time';
import type { MeddelandeTrådRad } from 'src/actions/meddelanden';
import type { TrädgårdsObjekt } from 'src/components/map/GoogleMap';

type Props = {
  open: boolean;
  onClose: () => void;
  poi: TrädgårdsObjekt | null;
  /** Namnet som visas i rubriken. Sätts av fastighetsmodalen, som vet vad ägaren delar. */
  rubrik?: string;
};

/**
 * Startar ett nytt samtal med ett hem från kartan.
 *
 * <p>Listan under formuläret är avsiktligt bara betraktarens egna samtal med hemmet — backend
 * lämnar inte ut grannarnas trådar. Läsning och svar sker på /meddelanden; modalen är ingången,
 * inte konversationsvyn.
 */
export default function HemMeddelandeModal({ open, onClose, poi, rubrik }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const router = useRouter();
  const [message, setMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [trådar, setTrådar] = useState<MeddelandeTrådRad[]>([]);
  const [isLoadingTrådar, setIsLoadingTrådar] = useState(false);

  const loadTrådar = async () => {
    if (!poi?.id) return;
    setIsLoadingTrådar(true);
    try {
      const response = await axios.get(endpoints.meddelanden.hemTrådar(poi.id));
      setTrådar(response.data || []);
    } catch (error) {
      console.error("Misslyckades att hämta samtal:", error);
    } finally {
      setIsLoadingTrådar(false);
    }
  };

  useEffect(() => {
    if (open && poi?.id) {
      loadTrådar();
      setMessage('');
    }
  }, [open, poi]);

  const handleSendMessage = async () => {
    if (!message.trim() || !poi?.id) return;
    setIsSending(true);
    try {
      // Bara texten. Avsändarnamnet slås upp på den inloggade i backend och tidpunkten sätts
      // där i svensk tid — klientens klocka och zon ska inte kunna avgöra vad som lagras.
      await axios.post(endpoints.meddelanden.skrivTillHem(poi.id), { text: message.trim() });
      enqueueSnackbar('Meddelande skickat till hemmet!', { variant: 'success' });
      setMessage('');
      loadTrådar();
    } catch (error: any) {
      console.error("Misslyckades att skicka meddelande:", error);
      // 403 betyder att ägaren stängt brevlådan för avsändaren — servern avgör, inte knappen.
      const nekad = error?.response?.status === 403;
      enqueueSnackbar(
        nekad ? 'Ägaren tar inte emot meddelanden från dig.' : 'Kunde inte skicka meddelandet.',
        { variant: 'error' }
      );
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1 }}>
        <Iconify icon="solar:mailbox-bold" width={28} color="primary.main" />
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Skicka meddelande till hemmet
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {rubrik ?? poi?.namn}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pb: 3 }}>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Meddelandet startar ett nytt samtal med de som bor här. Både du och de hittar det sedan
            under Meddelanden.
          </Typography>

          <TextField
            fullWidth
            multiline
            rows={3}
            label="Ditt meddelande"
            placeholder="Skriv ett meddelande..."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            disabled={isSending}
          />

          <Divider />

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
              Dina tidigare samtal med hemmet
            </Typography>

            {isLoadingTrådar ? (
              <Stack alignItems="center" sx={{ py: 2 }}>
                <CircularProgress size={24} />
              </Stack>
            ) : trådar.length === 0 ? (
              <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                Inga tidigare samtal.
              </Typography>
            ) : (
              <List
                sx={{
                  maxHeight: 180,
                  overflowY: 'auto',
                  bgcolor: 'background.neutral',
                  borderRadius: 1,
                  p: 1,
                  border: (theme) => `1px solid ${theme.palette.divider}`
                }}
              >
                {trådar.map((tråd) => (
                  <ListItem
                    key={tråd.id}
                    alignItems="flex-start"
                    sx={{ py: 0.5, cursor: 'pointer' }}
                    onClick={() => router.push(paths.dashboard.meddelanden.tråd(tråd.id))}
                    secondaryAction={
                      <Chip
                        size="small"
                        variant="soft"
                        color={tråd.antalSvar ? 'primary' : 'default'}
                        label={tråd.antalSvar === 1 ? '1 svar' : `${tråd.antalSvar} svar`}
                      />
                    }
                  >
                    <ListItemText
                      primary={tråd.initialtMeddelande?.text}
                      secondary={
                        <Typography variant="caption" color="text.secondary">
                          {fToNow(tråd.senasteAktivitet)}
                        </Typography>
                      }
                      primaryTypographyProps={{ variant: 'body2', noWrap: true }}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose} variant="outlined" color="inherit">
          Stäng
        </Button>
        <Button
          onClick={handleSendMessage}
          variant="contained"
          color="primary"
          disabled={isSending || !message.trim()}
          startIcon={isSending ? <CircularProgress size={20} color="inherit" /> : <Iconify icon="solar:outgoing-call-bold" />}
        >
          Skicka
        </Button>
      </DialogActions>
    </Dialog>
  );
}
